import { useCallback, useMemo, useState } from 'react';
import { createCustomColumn, createEnumColumn, createTextColumn, useTableState } from '@medprompt/shared';
import { MeasurementTypeEnum } from '../enums/measurementTypeEnum';
import { codeRecordFromSnomedSearchResult, codeRecordsEqual } from './configCodes';
import {
  MEASUREMENT_SIGNIFICANCE_FILTER_OPTIONS,
  renderMeasurementSignificanceBadge,
  renderOptionalCell,
} from './configTableCells';

const useMeasurementCodes = ({ baselineCodes, isEditing, saving }) => {
  const [measurementCodes, setMeasurementCodes] = useState({});
  const [measurementChanges, setMeasurementChanges] = useState({});
  const [editingMeasurement, setEditingMeasurement] = useState(null);

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows,
  } = useTableState('MeasurementCodesConfigTable', {
    defaultSortState: { columnKey: 'displayName', direction: 'asc' }
  });

  const applyFetched = useCallback((rawMap) => {
    const generatorMeasurements = rawMap || {};
    setMeasurementCodes(generatorMeasurements);
    setMeasurementChanges({});
    setEditingMeasurement(null);
    return { measurementCodes: { ...generatorMeasurements } };
  }, []);

  const snapshot = useCallback(() => ({
    measurementCodes: { ...measurementCodes },
  }), [measurementCodes]);

  const restore = useCallback((savedCodes) => {
    setMeasurementCodes(savedCodes);
    setMeasurementChanges({});
    setEditingMeasurement(null);
  }, []);

  const clearChanges = useCallback(() => {
    setMeasurementChanges({});
    setEditingMeasurement(null);
  }, []);

  const applyMeasurementCodeUpdate = useCallback((measurementType, updatedRecord) => {
    setMeasurementCodes((prev) => ({
      ...prev,
      [measurementType]: updatedRecord,
    }));
    setMeasurementChanges((prevCh) => {
      const next = { ...prevCh };
      const baseline = baselineCodes?.[measurementType];
      if (baseline && codeRecordsEqual(updatedRecord, baseline)) {
        delete next[measurementType];
      } else {
        next[measurementType] = updatedRecord;
      }
      return next;
    });
  }, [baselineCodes]);

  const handleMeasurementCodeSelect = (measurementType, rawResult) => {
    const normalized = codeRecordFromSnomedSearchResult(rawResult);
    if (!normalized) return;
    applyMeasurementCodeUpdate(measurementType, normalized);
    setEditingMeasurement(null);
  };

  const handleMeasurementCodeSave = useCallback((updatedRecord) => {
    if (!editingMeasurement) return;
    applyMeasurementCodeUpdate(editingMeasurement, updatedRecord);
    setEditingMeasurement(null);
  }, [editingMeasurement, applyMeasurementCodeUpdate]);

  const handleMeasurementRowClick = useCallback((row) => {
    if (!isEditing || saving) return;
    setEditingMeasurement(row.measurementType);
  }, [isEditing, saving]);

  const tableRows = useMemo(
    () =>
      Object.keys(MeasurementTypeEnum).map((measurementType) => {
        const currentCode = measurementCodes[measurementType];
        const preferredTerm =
          Array.isArray(currentCode?.terms) && currentCode.terms.length > 0
            ? currentCode.terms[0]
            : '';
        return {
          measurementType,
          displayName: MeasurementTypeEnum[measurementType].displayName,
          significance: MeasurementTypeEnum[measurementType].significance,
          code: currentCode?.code ?? '',
          preferredTerm,
        };
      }),
    [measurementCodes]
  );

  const columns = useMemo(
    () => [
      createTextColumn(
        'displayName',
        'Measurement',
        (row) => row.displayName,
        { sortable: true, filterable: true }
      ),
      createEnumColumn(
        'significance',
        'Code type',
        (row) => row.significance,
        MEASUREMENT_SIGNIFICANCE_FILTER_OPTIONS,
        {
          sortable: true,
          filterable: true,
          renderer: renderMeasurementSignificanceBadge,
          compareFn: (a, b) => {
            const order = { CORE: 0, EXTENDED: 1 };
            const aOrder = order[a.significance] ?? 999;
            const bOrder = order[b.significance] ?? 999;
            return aOrder - bOrder;
          },
        }
      ),
      createCustomColumn(
        'code',
        'SNOMED CT code',
        (row) => row.code ?? '',
        renderOptionalCell,
        { sortable: true, filterable: true }
      ),
      createCustomColumn(
        'preferredTerm',
        'Term',
        (row) => row.preferredTerm ?? '',
        renderOptionalCell,
        { sortable: true, filterable: true }
      ),
    ],
    []
  );

  return {
    measurementCodes,
    measurementChanges,
    editingMeasurement,
    setEditingMeasurement,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows,
    tableRows,
    columns,
    handleMeasurementCodeSelect,
    handleMeasurementCodeSave,
    handleMeasurementRowClick,
    applyFetched,
    snapshot,
    restore,
    clearChanges,
  };
};

export default useMeasurementCodes;
